import React, { useState } from "react";
import { Stepper } from "@jasper/shared/stories/Stepper/Stepper";
import {
  mapFieldsToValues,
  useWorkflow,
  useWorkflowFieldsAndDefaultValues,
} from "@jasper/shared/utils/workflows.utils";
import { Box, Loader, LoadingOverlay } from "@mantine/core";
import { useLocation } from "react-router-dom";
import { useCreateOrder } from "../useCreateOrder";
import { useAuthContext } from "/@/shared/contexts/AuthContext";
import { useCreateProduct } from "@jasper/shared/utils/createProduct.utils";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation } from "@apollo/client";
import { UPDATE_ORDER } from "@jasper/shared/gql/orders";
import {
  ItemType,
  Order,
  Product,
  updatePatient,
  updateProduct,
} from "@jasper/shared";
import {
  HooksAndOptionsMappedValues,
  InformationMappedValues,
  ProsthesisMappedValues,
} from "@jasper/shared/utils/savePartialDenture.utils";
import { PatientFormValues } from "@jasper/shared/utils/saveOrder.utils";

export const SteppedOrderCreationPage: React.FC = () => {
  const { state } = useLocation();
  const { user } = useAuthContext();
  if (!state || !state.id || !state.workflow) {
    console.error("No state found");
  }

  const [page, setPage] = useState(0);
  const [productIndex, setProductIndex] = useState(0);

  const { existingDraftOrderLoading, order, refetchOrder } = useCreateOrder(
    state ? { newOrderId: state.id } : undefined
  );

  const { removableItemChoicesData, removableItemChoicesLoading } =
    useCreateProduct(state.workflow);

  const workflow = useWorkflowFieldsAndDefaultValues(state.workflow);
  const methods = useForm({
    defaultValues: workflow.workflowDefaultValues,
  });

  const product = (order?.products?.[productIndex] as unknown as Product) ?? {};

  const updateOrderWrapper = async (data: { variables: any }) => {
    const result = await updateOrder(data);
    return result.data?.updateOrder as Order;
  };

  const save = async (fields: string[], values: string[]) => {
    const mappedValues = mapFieldsToValues(fields, values);
    const currentProduct: ItemType = state.workflow[productIndex];
    const currentStepLabel = useWorkflow([state.workflow])[page].label;
    if (currentStepLabel === "Patient") {
      await updatePatient(
        updateOrderWrapper,
        mappedValues as PatientFormValues,
        state.id
      );
    } else {
      await updateProduct(
        updateOrderWrapper,
        mappedValues as
          | InformationMappedValues
          | ProsthesisMappedValues
          | HooksAndOptionsMappedValues,
        state.id,
        refetchOrder as unknown as () => Promise<Order>,
        currentProduct,
        currentStepLabel,
        product
      );
    }
  };

  const nextStep = async () => {
    const stepFields = workflow.workflowFields[productIndex];
    const currentField = stepFields[page];
    const fields = Object.keys(currentField);
    const values = methods.getValues(fields);
    const isValid = await methods.trigger(fields);
    if (isValid) {
      await save(fields, values);
      setPage(page + 1);
    }
  };

  const workflowToDisplay =
    order && order !== undefined
      ? useWorkflow(
          [state.workflow],
          order as any,
          user as any,
          removableItemChoicesData,
          nextStep,
          product
        )
      : null;

  const isLoading = existingDraftOrderLoading || removableItemChoicesLoading;

  const [updateOrder] = useMutation(UPDATE_ORDER);

  const onSubmit = (data: any) => {
    if (productIndex < workflow.workflowFields.length - 1) {
      setProductIndex(productIndex + 1);
    }
    console.log("Final Form Data:", data);
  };

  return (
    <Box p="lg">
      <LoadingOverlay visible={isLoading}>
        <Loader />
      </LoadingOverlay>
      {!isLoading && workflowToDisplay && (
        <FormProvider {...methods}>
          <form onSubmit={methods.handleSubmit(onSubmit)}>
            <Stepper
              variant="textDown"
              data={workflowToDisplay}
              page={page}
              setPage={setPage}
            />
          </form>
        </FormProvider>
      )}
    </Box>
  );
};
